import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestProps } from '@/utils';

/** Layout: `1`-`9`, decimal point, `0`, backspace. */
const NUMPAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0'];

export type NumpadProps = {
    onKey: (key: string) => void;
    onDelete: () => void;
    deleteDisabled: boolean;
    /** The key whose physical twin was just pressed (`usePaymentPhysicalKeys`) — flashed briefly so the two inputs read as one. */
    flashedKey?: string | null;
};

/**
 * Shared tablet-friendly numpad (render-only): tall, filled keys sized for touch.
 * Carries no value of its own — the owning flow routes the presses (to the cash
 * tender, or to the active combined leg).
 *
 * Out of the Tab order (bos-seller#58, matching `PinPad`): keyboard parity
 * comes from `usePaymentPhysicalKeys` feeding this same `onKey`/`onDelete`
 * path, not from tabbing across the pad — but the keys stay fully clickable
 * and touchable, and flash briefly when `flashedKey` names them. A tap never
 * takes focus (`preventFocusOnPress`, SEL-50): a focused key would swallow
 * the next physical `Enter` as a second press of itself instead of confirming.
 */
export const Numpad: React.FC<NumpadProps> = ({
    onKey,
    onDelete,
    deleteDisabled,
    flashedKey = null,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="grid grid-cols-3 gap-2.5">
            {NUMPAD_KEYS.map((key) => (
                <Button
                    key={key}
                    className="h-14 text-2xl font-semibold tabular-nums"
                    {...getNodeTestProps(`seller-pay-numpad-${key}`)}
                    excludeFromTabOrder
                    fullWidth
                    onPress={() => onKey(key)}
                    preventFocusOnPress
                    variant={flashedKey === key ? 'primary' : 'secondary'}
                >
                    {key}
                </Button>
            ))}
            <Button
                aria-label={t('seller.payment.backspace')}
                className="h-14"
                {...getNodeTestProps('seller-pay-numpad-backspace')}
                excludeFromTabOrder
                fullWidth
                isDisabled={deleteDisabled}
                isIconOnly
                onPress={onDelete}
                preventFocusOnPress
                variant={flashedKey === 'backspace' ? 'primary' : 'secondary'}
            >
                <Icon
                    className="size-6 text-muted"
                    icon="solar:backspace-linear"
                />
            </Button>
        </div>
    );
};
